import React, { useState, useEffect } from 'react';
import { Check, X, Edit2, Loader2, AlertCircle } from 'lucide-react';
import { EditableParameter, validateParameterValue } from '../config/editableParameters';

interface EditableConfigFieldProps {
  parameter: EditableParameter;
  currentValue: any;
  pendingValue?: any;
  status?: 'normal' | 'pending' | 'applied' | 'failed';
  error?: string;
  onSave: (value: any) => Promise<void>;
  disabled?: boolean;
}

export const EditableConfigField: React.FC<EditableConfigFieldProps> = ({
  parameter,
  currentValue,
  pendingValue,
  status = 'normal',
  error,
  onSave,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      // Convert value to appropriate format for editing
      if (parameter.type === 'boolean') {
        setEditValue(currentValue ? 'true' : 'false');
      } else if (parameter.convertToMinutes && parameter.type === 'number') {
        // Convert seconds to minutes for display
        const seconds = Number(currentValue || 0);
        setEditValue(String(Math.round(seconds / 60)));
      } else {
        setEditValue(String(currentValue || ''));
      }
    }
  }, [isEditing, currentValue, parameter]);

  const handleEdit = () => {
    if (!disabled && status !== 'pending') {
      setIsEditing(true);
      setValidationError(null);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
    setValidationError(null);
  };

  const handleSave = async () => {
    let valueToSave = editValue;

    // Convert to appropriate type
    let finalValue: any = valueToSave;
    if (parameter.type === 'number') {
      finalValue = Number(valueToSave);
      // Convert minutes to seconds if needed
      if (parameter.convertToMinutes) {
        finalValue = finalValue * 60; // Convert minutes to seconds
      }
    } else if (parameter.type === 'boolean') {
      finalValue = valueToSave === 'true' || valueToSave === '1';
    }

    // Validate
    const validation = validateParameterValue(parameter.key, finalValue);
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid value');
      return;
    }

    // Save
    setIsSaving(true);
    try {
      await onSave(finalValue);
      setIsEditing(false);
      setEditValue('');
      setValidationError(null);
    } catch (err) {
      setValidationError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const displayValue = () => {
    const value = pendingValue !== undefined ? pendingValue : currentValue;
    
    if (value === null || value === undefined || value === '') return '—';
    
    if (parameter.type === 'boolean') {
      const isEnabled = value === true || value === 1 || value === '1' || value === 'true';
      return isEnabled ? 'Enabled' : 'Disabled';
    }
    
    // Display time intervals in a user-friendly format
    if ((parameter.unit === 'seconds' || parameter.unit === 'minutes') && parameter.type === 'number') {
      const seconds = Number(value);
      
      // If convertToMinutes is true, the value is already in seconds
      if (parameter.convertToMinutes) {
        if (seconds >= 3600) {
          const hours = Math.floor(seconds / 3600);
          const mins = Math.floor((seconds % 3600) / 60);
          return mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
        } else if (seconds >= 60) {
          return `${Math.round(seconds / 60)} minutes`;
        } else {
          return `${seconds} seconds`;
        }
      } else {
        // If not convertToMinutes, assume value is already in the correct unit
        return parameter.unit ? `${value} ${parameter.unit}` : value;
      }
    }
    
    return parameter.unit ? `${value} ${parameter.unit}` : value;
  };

  const getStatusColor = () => {
    switch (status) {
      case 'pending':
        return 'bg-amber-50 border-amber-200';
      case 'applied':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return '';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
        return <Loader2 className="w-3 h-3 text-amber-600 animate-spin" />;
      case 'applied':
        return <Check className="w-3 h-3 text-green-600" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-600" />;
      default:
        return null;
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">
          {parameter.type === 'boolean' ? (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSaving}
            >
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          ) : (
            <div>
              <input
                type={parameter.type === 'number' ? 'number' : 'text'}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                min={parameter.min}
                max={parameter.max}
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={parameter.unit}
                disabled={isSaving}
              />
              {validationError && (
                <div className="text-xs text-red-600 mt-1">{validationError}</div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
          title="Save"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between text-xs rounded px-1 py-0.5 transition-colors ${getStatusColor()}`}>
      <span className="text-gray-600">{parameter.label}</span>
      <div className="flex items-center gap-1">
        {getStatusIcon()}
        <span className={`font-medium ${status === 'failed' ? 'text-red-700' : 'text-gray-900'}`}>
          {displayValue()}
        </span>
        {!disabled && status !== 'pending' && (
          <button
            onClick={handleEdit}
            className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            title={`Edit ${parameter.label}`}
          >
            <Edit2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {status === 'pending' && pendingValue !== undefined && pendingValue !== currentValue && (
        <div className="text-xs text-amber-600 ml-2">
          (was: {currentValue})
        </div>
      )}
      {status === 'failed' && error && (
        <div className="text-xs text-red-600 ml-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default EditableConfigField;